import { useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Lock, ArrowDown } from "lucide-react";
import { scrollToId } from "@/lib/scroll";

const activityOptions = [
  { label: "Sedentario", factor: 1.2 },
  { label: "Leggero (1-3 allenamenti)", factor: 1.375 },
  { label: "Moderato (3-5 allenamenti)", factor: 1.55 },
  { label: "Intenso (6-7 allenamenti)", factor: 1.725 },
  { label: "Atleta (2 sessioni/giorno)", factor: 1.9 },
];

const foods = [
  { name: "Petto di pollo", protein: 31 },
  { name: "Fesa di tacchino", protein: 29 },
  { name: "Manzo magro", protein: 26 },
  { name: "Tonno al naturale", protein: 25.5 },
  { name: "Salmone", protein: 20.5 },
  { name: "Merluzzo", protein: 17 },
  { name: "Uova intere", protein: 12.5 },
  { name: "Ricotta", protein: 11 },
  { name: "Lenticchie secche", protein: 25 },
  { name: "Ceci secchi", protein: 20 },
];

const inputCls =
  "w-full bg-ink-deep border border-white/15 focus:border-blood outline-none px-3 py-2.5 text-white text-sm font-mono transition-colors";

export default function ExcelBonusSection() {
  const [weight, setWeight] = useState(75);
  const [height, setHeight] = useState(178);
  const [age, setAge] = useState(30);
  const [sex, setSex] = useState("M");
  const [activity, setActivity] = useState(1.55);

  const tdee = useMemo(() => {
    const bmr = sex === "M" ? 10 * weight + 6.25 * height - 5 * age + 5 : 10 * weight + 6.25 * height - 5 * age - 161;
    return Math.round(bmr * activity);
  }, [weight, height, age, sex, activity]);

  const [baseFood, setBaseFood] = useState(foods[0].name);
  const [grams, setGrams] = useState(150);
  const [altFood, setAltFood] = useState(foods[4].name);

  const base = foods.find((f) => f.name === baseFood);
  const alt = foods.find((f) => f.name === altFood);
  const altGrams = Math.round((grams * base.protein) / alt.protein);

  return (
    <section id="bonus-excel" className="py-24 px-5 sm:px-8 border-y border-white/10 relative overflow-hidden">
      <div
        aria-hidden="true"
        className="absolute inset-0 pointer-events-none"
        style={{ background: "radial-gradient(ellipse 50% 60% at 50% 0%, rgba(229,9,20,0.1), transparent 60%)" }}
      />
      <div className="max-w-7xl mx-auto relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 28 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.15 }}
          transition={{ duration: 0.7 }}
          className="max-w-3xl"
        >
          <p className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.3em] text-blood font-semibold border border-blood/50 bg-blood/10 px-3 py-1.5 mb-5">
            <Lock size={12} /> Bonus esclusivo acquirenti
          </p>
          <h2 className="font-display font-black uppercase text-4xl sm:text-5xl lg:text-6xl leading-[0.95]">
            File Excel <span className="text-blood">interattivo</span>
          </h2>
          <p className="mt-4 text-neutral-300 text-base sm:text-lg leading-relaxed">
            Calcolo del fabbisogno giornaliero e sostituzione dei pasti in autonomia: modifichi un
            alimento e il file ricalcola tutto da solo. <strong className="text-white">Incluso in ogni pacchetto.</strong>
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 gap-5 mt-12">
          <motion.div
            initial={{ opacity: 0, y: 28 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.15 }}
            transition={{ duration: 0.6 }}
            className="bg-ink-surface border border-white/10 p-6 sm:p-8"
            data-testid="excel-tdee-widget"
          >
            <div className="flex items-center justify-between mb-6">
              <h3 className="font-display font-extrabold uppercase text-2xl tracking-tight">Calcolatore TDEE</h3>
              <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-neutral-400 border border-white/15 px-2 py-1">Demo</span>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              <label className="block">
                <span className="font-mono text-[10px] uppercase tracking-wider text-neutral-400">Peso (kg)</span>
                <input data-testid="tdee-weight-input" type="number" min="30" max="250" value={weight} onChange={(e) => setWeight(+e.target.value || 0)} className={inputCls + " mt-1"} />
              </label>
              <label className="block">
                <span className="font-mono text-[10px] uppercase tracking-wider text-neutral-400">Altezza (cm)</span>
                <input data-testid="tdee-height-input" type="number" min="120" max="230" value={height} onChange={(e) => setHeight(+e.target.value || 0)} className={inputCls + " mt-1"} />
              </label>
              <label className="block">
                <span className="font-mono text-[10px] uppercase tracking-wider text-neutral-400">Età</span>
                <input data-testid="tdee-age-input" type="number" min="12" max="99" value={age} onChange={(e) => setAge(+e.target.value || 0)} className={inputCls + " mt-1"} />
              </label>
              <label className="block">
                <span className="font-mono text-[10px] uppercase tracking-wider text-neutral-400">Sesso</span>
                <select data-testid="tdee-sex-select" value={sex} onChange={(e) => setSex(e.target.value)} className={inputCls + " mt-1"}>
                  <option value="M">Uomo</option>
                  <option value="F">Donna</option>
                </select>
              </label>
              <label className="block col-span-2 sm:col-span-2">
                <span className="font-mono text-[10px] uppercase tracking-wider text-neutral-400">Attività</span>
                <select data-testid="tdee-activity-select" value={activity} onChange={(e) => setActivity(+e.target.value)} className={inputCls + " mt-1"}>
                  {activityOptions.map((a) => (
                    <option key={a.factor} value={a.factor}>{a.label}</option>
                  ))}
                </select>
              </label>
            </div>
            <div className="mt-6 border-t border-white/10 pt-5 flex items-end justify-between">
              <div>
                <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-blood font-semibold">Fabbisogno giornaliero</p>
                <p className="font-display font-black text-5xl sm:text-6xl leading-none mt-1" data-testid="tdee-result">
                  {tdee} <span className="text-neutral-400 text-2xl">kcal</span>
                </p>
              </div>
              <p className="font-mono text-[9px] text-neutral-500 uppercase tracking-wider text-right max-w-[140px]">Versione completa con macro e pasti nel file Excel</p>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 28 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.15 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="bg-ink-surface border border-white/10 p-6 sm:p-8 relative"
            data-testid="excel-substitute-widget"
          >
            <div className="flex items-center justify-between mb-6">
              <h3 className="font-display font-extrabold uppercase text-2xl tracking-tight">Sostituzione pasti</h3>
              <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-neutral-400 border border-white/15 px-2 py-1">Demo</span>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <label className="block col-span-2">
                <span className="font-mono text-[10px] uppercase tracking-wider text-neutral-400">Hai previsto…</span>
                <select data-testid="sub-base-select" value={baseFood} onChange={(e) => setBaseFood(e.target.value)} className={inputCls + " mt-1"}>
                  {foods.map((f) => <option key={f.name} value={f.name}>{f.name}</option>)}
                </select>
              </label>
              <label className="block col-span-2 sm:col-span-1">
                <span className="font-mono text-[10px] uppercase tracking-wider text-neutral-400">Grammi</span>
                <input data-testid="sub-grams-input" type="number" min="10" max="1000" value={grams} onChange={(e) => setGrams(+e.target.value || 0)} className={inputCls + " mt-1"} />
              </label>
              <label className="block col-span-2 sm:col-span-1">
                <span className="font-mono text-[10px] uppercase tracking-wider text-neutral-400">Ma in casa hai…</span>
                <select data-testid="sub-alt-select" value={altFood} onChange={(e) => setAltFood(e.target.value)} className={inputCls + " mt-1"}>
                  {foods.map((f) => <option key={f.name} value={f.name}>{f.name}</option>)}
                </select>
              </label>
            </div>
            <div className="mt-6 border-t border-white/10 pt-5">
              <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-blood font-semibold">Equivalenza esatta</p>
              <p className="font-display font-bold text-2xl sm:text-3xl leading-tight mt-1" data-testid="sub-result">
                {grams}g di {base.name} ≈ <span className="text-blood">{altGrams}g</span> di {alt.name}
              </p>
              <p className="mt-2 text-neutral-400 text-sm">Stesse proteine, zero pensieri. L'algoritmo completo con kcal e macro è nel file.</p>
            </div>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.6 }}
          className="mt-10 flex flex-col sm:flex-row items-start sm:items-center gap-5"
        >
          <motion.button
            data-testid="excel-unlock-cta"
            whileHover={{ scale: 1.04 }}
            whileTap={{ scale: 0.97 }}
            onClick={() => scrollToId("pacchetti")}
            className="bg-blood hover:bg-blood-hot text-white font-display font-bold uppercase tracking-wider text-xl px-9 py-4 red-glow transition-colors inline-flex items-center gap-3"
          >
            Sbloccalo con un pacchetto <ArrowDown size={18} />
          </motion.button>
          <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-400 max-w-xs leading-relaxed">
            Non acquistabile separatamente. Lo ricevi su WhatsApp dopo l'acquisto.
          </p>
        </motion.div>
      </div>
    </section>
  );
}
